import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, BrainCircuit, CalendarClock, Handshake, PlayCircle } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { AccountShell, Panel } from "@/components/site/AccountShell";
import { Loading, ProblemAlert } from "@/components/site/Feedback";
import { AppointmentStatusBadge } from "@/components/site/Booking";
import { SubscriptionSummary } from "@/routes/account/billing";
import { api } from "@/lib/api";
import { minutesLabel, timeRange } from "@/lib/format";

const shortcuts = [
  { to: "/academy", icon: PlayCircle, title: "Video library", body: "Continue the Academy lessons." },
  { to: "/deal-analyzer", icon: BrainCircuit, title: "Deal Analyzer", body: "Ask George about a property." },
  { to: "/mentorship", icon: Handshake, title: "Mentorship", body: "Book a session with your mentor." },
  { to: "/blueprint", icon: BookOpen, title: "The Blueprint", body: "Read or download the book." },
] as const;

export function DashboardPage() {
  usePageTitle("Dashboard");
  const overview = useQuery({ queryKey: ["billing", "overview"], queryFn: api.billing.overview });

  return (
    <AccountShell title="Your dashboard" subtitle="Your membership, credits and upcoming sessions at a glance.">
      {overview.isPending ? (
        <Loading />
      ) : overview.isError ? (
        <ProblemAlert error={overview.error} />
      ) : (
        <div className="space-y-8">
          <div className="grid gap-6 lg:grid-cols-3">
            <Panel title="Membership" className="lg:col-span-1">
              <SubscriptionSummary subscription={overview.data.subscription} compact />
            </Panel>
            <Panel title="Mentorship credits">
              <p className="font-display text-6xl">{minutesLabel(overview.data.credits.mentorship)}</p>
              <p className="text-sm text-muted-foreground">mentorship time available</p>
              <Button asChild variant="outlineGold" size="sm" className="mt-5">
                <Link to="/mentorship">Book or buy hours</Link>
              </Button>
            </Panel>
            <Panel title="Analyzer credits">
              <p className="font-display text-6xl">{overview.data.credits.analyzer}</p>
              <p className="text-sm text-muted-foreground">questions left</p>
              <Button asChild variant="outlineGold" size="sm" className="mt-5">
                <Link to="/deal-analyzer">Open the Analyzer</Link>
              </Button>
            </Panel>
          </div>

          <Panel
            title="Upcoming sessions"
            action={
              <Link to="/mentorship" className="text-sm text-muted-foreground underline-offset-4 hover:underline">
                All appointments
              </Link>
            }
          >
            {overview.data.upcomingAppointments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming sessions.</p>
            ) : (
              <ul className="divide-y divide-border">
                {overview.data.upcomingAppointments.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center gap-3 py-3">
                    <CalendarClock className="size-4 text-gold" />
                    <span className="text-sm font-medium">{timeRange(a.startTime, a.endTime)}</span>
                    <AppointmentStatusBadge status={a.status} />
                    {a.topic && <span className="text-sm text-muted-foreground">{a.topic}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {shortcuts.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group rounded-lg border border-border bg-card p-6 shadow-card transition-all hover:-translate-y-1 hover:border-gold/50"
              >
                <s.icon className="size-5 text-gold" />
                <h3 className="mt-4 text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                <ArrowRight className="mt-4 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </AccountShell>
  );
}
